import { ReactNode, CSSProperties, useMemo, useRef } from 'react';
import { AnimationProps } from 'framer-motion';
import { buildColumnWidthCSS } from './columnWidthStyles';
import NewTableHeader from './NewTableHeader';
import NewTablePagination from './NewTablePagination';
import NewTableRow from './NewTableRow';
import { NewTableColumn, SortDirection } from './types';

let tableInstanceCounter = 0;

function useTableInstanceId(): string {
  const idRef = useRef<string | null>(null);
  if (idRef.current === null) {
    idRef.current = `t${tableInstanceCounter++}`;
  }
  return idRef.current;
}

interface NewTableProps<T> {
  columns: NewTableColumn<T>[];
  columnVisibility?: Record<string, boolean>;
  data: T[];
  getRowKey: (row: T, index: number) => string | number;
  isLoading?: boolean;
  onRowClick?: (row: T) => void;
  renderRowActions?: (row: T) => ReactNode;
  /** Contenido opcional renderizado arriba de cada fila (ej. banners de estado). */
  renderRowBanner?: (row: T, index: number) => ReactNode;
  /** Clases extra por fila según su estado (ej. resaltado). Se mergean con las base. */
  getRowClassName?: (row: T, index: number) => string;
  /** Función opcional para inyectar animaciones y estilos personalizados a cada fila desde el exterior */
  getRowAnimation?: (
    row: T,
    index: number
  ) => {
    initial?: AnimationProps['initial'];
    animate?: AnimationProps['animate'];
    transition?: AnimationProps['transition'];
    style?: CSSProperties; // <--- Añadido para recibir estilos en línea (como la animación de pulso)
  };
  actionsWidth?: string;
  sortBy?: string;
  sortDirection?: SortDirection;
  onSortChange?: (columnKey: string, direction: SortDirection) => void;
  page: number;
  total: number;
  rowsPerPage: number;
  onChangePage: (page: number) => void;
  onChangeRowsPerPage?: (rowsPerPage: number) => void;
  rowsPerPageOptions?: number[];
  stickyHeader?: { top: string; zIndex: number };
  emptyTitle: string;
}

function NewTable<T>({
  columns,
  columnVisibility,
  data,
  getRowKey,
  isLoading = false,
  onRowClick,
  renderRowActions,
  renderRowBanner,
  getRowClassName,
  getRowAnimation,
  actionsWidth = '2.5rem',
  sortBy,
  sortDirection,
  onSortChange,
  page,
  total,
  rowsPerPage,
  onChangePage,
  onChangeRowsPerPage,
  rowsPerPageOptions,
  stickyHeader,
  emptyTitle
}: NewTableProps<T>) {
  const tableId = useTableInstanceId();
  const visibleColumns = columns.filter(
    (column) => columnVisibility?.[column.key] !== false
  );
  const hasRowActions = Boolean(renderRowActions);

  const columnWidthCSS = useMemo(
    () =>
      buildColumnWidthCSS(
        tableId,
        visibleColumns,
        hasRowActions ? { width: actionsWidth } : undefined
      ),
    [tableId, visibleColumns, hasRowActions, actionsWidth]
  );

  return (
    <div className="flex flex-col w-full h-full bg-page-light dark:bg-page-dark">
      <style>{columnWidthCSS}</style>
      <div
        className={`${
          stickyHeader ? 'sticky' : ''
        } bg-page-light dark:bg-page-dark`}
        style={{
          zIndex: stickyHeader?.zIndex ?? 0,
          top: stickyHeader?.top ?? '0',
          marginBottom: '4px'
        }}
      >
        <div className="w-full">
          <NewTableHeader
            columns={visibleColumns}
            tableId={tableId}
            hasRowActions={hasRowActions}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSortChange={onSortChange}
          />
        </div>
      </div>
      {/* w-max min-w-full: este contenedor mide lo mismo que la fila más ancha, así
          el paginador (sticky) tiene un bloque contenedor que abarca todo el
          recorrido del scroll horizontal. */}
      <div className="flex w-max min-w-full flex-col">
        <div data-table-body={tableId}>
          <div className="flex flex-col gap-3 pb-3">
            {data.length > 0 ? (
              data.map((row, index) => {
                const customAnimation = getRowAnimation
                  ? getRowAnimation(row, index)
                  : {};

                return (
                  <div key={getRowKey(row, index)} className="flex flex-col">
                    {renderRowBanner && renderRowBanner(row, index)}
                    <NewTableRow
                      row={row}
                      index={index}
                      columns={visibleColumns}
                      tableId={tableId}
                      onRowClick={onRowClick}
                      renderRowActions={renderRowActions}
                      rowClassName={
                        getRowClassName && getRowClassName(row, index)
                      }
                      initialAnimation={customAnimation.initial}
                      animateAnimation={customAnimation.animate}
                      transitionAnimation={customAnimation.transition}
                      customStyle={customAnimation.style} // <--- Pasamos el estilo hacia la fila
                    />
                  </div>
                );
              })
            ) : (
              <div className="flex flex-col items-center justify-center">
                <p>{emptyTitle}</p>
              </div>
            )}
          </div>
        </div>

        {total > 0 && (
          <NewTablePagination
            page={page}
            total={total}
            rowsPerPage={rowsPerPage}
            onChangePage={onChangePage}
            onChangeRowsPerPage={onChangeRowsPerPage}
            rowsPerPageOptions={rowsPerPageOptions}
            disable={isLoading}
          />
        )}
      </div>
    </div>
  );
}

export default NewTable;
export type { NewTableColumn, SortDirection } from './types';
